import { Button, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { renderClose, useDialog } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propRequired, propMinValue, propLengthMax, propValidValue, propValueValidator, propCurrency } from "../../common/entityStoreValidators";
import { ReplacementArticleDto, ReplacementArticleEditDto } from "./types";
import { UseListStore, createListStore } from "../../common/listStore";
import { EnumItemDto } from "../../common/enum";
import { map } from "rxjs";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { ReplacementArticleDetailDialog } from "./ReplacementArticleDetailDialog";
import { Add, Edit, Remove } from "@mui/icons-material";
import { orderBy } from "lodash";

export const ReplacementArticleDialog = (props: {
    close: () => void;
    replacementArticle: ReplacementArticleEditDto | null;
    onChange(): void;
    useListStore: UseListStore<ReplacementArticleDto>;
    useSaleArticlesStore: UseListStore<EnumItemDto>;
}) => {
    const [useStore] = useState(() => createEntityStore<ReplacementArticleEditDto>({
        data: props.replacementArticle == null ? { isActive: true } : { ...props.replacementArticle },
        path: "settings/replacementArticles",
        fetchOnInit: false,
        id: props.replacementArticle?.id ?? null,
        getNewId: p => p.id!,
        onSaved() {
            props.onChange();
            props.close();
        },
        propValidators: [
            propLengthMax("description", 100),
            propRequired("saleArticleId"),
            propValueValidator("saleArticleId", (p, e) =>
                e.isActive && props.useListStore.getState().items.some(r => r.isActive && r.saleArticleId === p && (e.id == null || e.id !== r.id)) &&
                "This article has already replacement article."),
            propValidValue("price", "priceFormatted"),
            propCurrency("price"),
            propMinValue("price", 0),
        ]
    }));
    const [useDetailsStore] = useState(() => createListStore({
        data: useStore.getState().clientData$.pipe(map(p => ({ items: orderBy(p.data.details || [], p => p.priority), isFetching: p.isFetching }))),
        listProps: [
            { column: "saleArticleId", label: "Sales article", flex: 1, enumStore: props.useSaleArticlesStore },
            { column: "priority", label: "Priority", width: 90, type: "number" },
            { column: "price", label: "Price", width: 100, type: "currency" },
            { column: "oldQuantity", label: "Orig quantity", width: 90, type: "number" },
            { column: "newQuantity", label: "New quantity", width: 90, type: "number" },
        ],
    }));
    const detailsStore = useDetailsStore(p => ({ selectedItem: p.selectedItem }));
    const newDetailDialog = useDialog(close => <ReplacementArticleDetailDialog close={close} detail={null} useReplacementArticleStore={useStore}
        useSaleArticlesStore={props.useSaleArticlesStore} />, "sm");
    const editDetailDialog = useDialog(close => <ReplacementArticleDetailDialog close={close} detail={detailsStore.selectedItem!} useReplacementArticleStore={useStore}
        useSaleArticlesStore={props.useSaleArticlesStore} />, "sm");
    function removeSelectedDetail() {
        const details = useStore.getState().clientData.details || [];
        useStore.getState().setClientDataProp("details", details.filter(p => p.id !== detailsStore.selectedItem!.id));
    }
    const { save, entityId, isSaveDisabled } = useStore(p => ({ save: p.save, entityId: p.entityId, isSaveDisabled: p.isSaveDisabled }));
    return <>
        {newDetailDialog.render()}
        {editDetailDialog.render()}
        <DialogTitle>{entityId == null ? `New replacement article` : `Edit replacement article`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4} alignItems="flex-start">
                <Grid item container columnSpacing={2} xs={5}>
                    <Grid item xs={12}>{propEnumField(useStore)("saleArticleId", "Sales article", props.useSaleArticlesStore)}</Grid>
                    <Grid item xs={12}>{propTextField(useStore)("description", "Description")}</Grid>
                    <Grid item xs={6}>{propNumberField(useStore)("price", "priceFormatted", "Price", { isCurrency: true })}</Grid>
                    <Grid item xs={6}>{propBoolField(useStore)("isActive", "Active")}</Grid>
                </Grid>
                <Grid item xs={7}>
                    <Typography variant="h6">Details</Typography>
                    {renderListStore(useDetailsStore, {
                        header: listHeaderItems([
                            ["edit", () => <Button color="edit" title="Edit" disabled={detailsStore.selectedItem == null} onClick={() => editDetailDialog.show()}><Edit /></Button>],
                            ["add", () => <Button color="add" title="Add" onClick={() => newDetailDialog.show()}><Add /></Button>],
                            ["remove", () => <Button color="delete" disabled={detailsStore.selectedItem == null} title="Remove" onClick={removeSelectedDetail}><Remove /></Button>],
                        ]), onDoubleClick: () => editDetailDialog.show(),
                    })}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={save} disabled={isSaveDisabled} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};